import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Buton, Kart, Secim } from "@kantin/ui";
import { apiOku, apiYaz } from "./api.js";
import type { Urun } from "./kancalar.js";
import { IslemDurumu, MetinAlani, TehlikeliOnay } from "./ortak.js";

interface StokYaniti {
  urunler: {
    urun_id: string;
    toplam: string;
    dagilim: { nokta_id: string; nokta_ad: string; durum: number; durum_adi: string; miktar: string }[];
  }[];
}

export function UrunDurumOnayi({ urun, kapat, kaydedildi }: {
  urun: Urun;
  kapat: () => void;
  kaydedildi: () => void;
}) {
  const istemci = useQueryClient();
  const alan = useRef<HTMLDivElement>(null);
  const [gerekce, setGerekce] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [hata, setHata] = useState("");
  const stok = useQuery({
    queryKey: ["urun-durum-stok", urun.id],
    queryFn: () => apiOku<StokYaniti>("/stok", { urun_id: urun.id }),
    enabled: urun.aktif,
    staleTime: 0,
    retry: false,
  });
  const islem = useMutation({
    mutationFn: () => apiYaz<Urun>("PATCH", `/urun/${urun.id}`, {
      aktif: !urun.aktif,
      ...(urun.aktif ? { pasif_gerekcesi: gerekce === "Diğer" ? aciklama.trim() : gerekce } : {}),
    }),
    onSuccess: async () => {
      await Promise.all([
        istemci.invalidateQueries({ queryKey: ["urun"] }),
        istemci.invalidateQueries({ queryKey: ["kategori"] }),
        istemci.invalidateQueries({ queryKey: ["m8", "audit"] }),
      ]);
      kaydedildi();
    },
  });

  useEffect(() => {
    alan.current?.focus();
    alan.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const kaydet = () => {
    if (islem.isPending) return;
    if (urun.aktif) {
      if (!stok.isSuccess || stok.isFetching) {
        setHata("Stok bilgisi yüklenmeden pasife alınamaz.");
        return;
      }
      const metin = gerekce === "Diğer" ? aciklama.trim() : gerekce;
      if (metin.length < 5 || metin.length > 500) {
        setHata("Bir gerekçe seçin; Diğer için 5–500 karakter açıklama yazın.");
        return;
      }
    }
    setHata("");
    islem.mutate();
  };
  const kayit = stok.data?.urunler.find((u) => u.urun_id === urun.id);

  return (
    <div ref={alan} tabIndex={-1}>
      <Kart baslik="Ürün durumu">
        <TehlikeliOnay
          soru={`${urun.ad} ${urun.aktif ? "pasife alınsın mı?" : "aktifleştirilsin mi?"}`}
          aciklama={urun.aktif
            ? "Ürün tüm kantinlerde satışa kapanır. Geçmiş satışlar ve stok kayıtları korunur. Çevrimdışı kasalar sonraki senkronizasyonda güncellenir; bekleyen satışları önce aktarın."
            : "Ürün yeniden aktif olur. Noktaya özel satış kısıtları korunur; kasalar senkronizasyonla güncellenir."}
          onayMetni={urun.aktif ? "Evet, pasife al" : "Evet, aktifleştir"}
          onayla={kaydet}
          vazgec={kapat}
          yukleniyor={islem.isPending}
          cocuk={urun.aktif ? (
            <div className="flex flex-col gap-3">
              {stok.isPending && <p role="status">Stok bilgisi yükleniyor…</p>}
              {stok.isError && <div>
                <IslemDurumu hata={stok.error} />
                <Buton onClick={() => void stok.refetch()}>Stoku tekrar yükle</Buton>
              </div>}
              {stok.isSuccess && <div>
                <p className="m-0 font-semibold">Kalan stok: {kayit?.toplam ?? "0.000"} {urun.birim}</p>
                <p className="text-sm">Yalnızca yetkili olduğunuz noktaların stoğu gösterilir. Stok varsa iade, devir veya düzeltme işlemini planlayın.</p>
                {kayit && kayit.dagilim.length > 0 && <ul>
                  {kayit.dagilim.map((s) => <li key={`${s.nokta_id}-${s.durum}`}>
                    {s.nokta_ad} · {s.durum_adi}: {s.miktar} {urun.birim}
                  </li>)}
                </ul>}
              </div>}
              <Secim
                etiket="Pasife alma gerekçesi"
                value={gerekce}
                onChange={(e) => { setGerekce(e.target.value); setHata(""); }}
                disabled={islem.isPending}
                secenekler={[
                  { deger: "", metin: "Gerekçe seçin" },
                  ...["Mükerrer", "Hatalı kayıt", "Satıştan kaldırıldı", "Diğer"].map((s) => ({ deger: s, metin: s })),
                ]}
              />
              {gerekce === "Diğer" && <MetinAlani
                etiket="Pasife alma açıklaması"
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
                maxLength={500}
                disabled={islem.isPending}
              />}
            </div>
          ) : undefined}
        />
        {hata && <p role="alert" className="text-hata">{hata}</p>}
        <IslemDurumu hata={islem.error} />
      </Kart>
    </div>
  );
}
